import { randPastDate, rand, randTextRange } from '@ngneat/falso'
import { Factory, Model, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'
import {
  getCurrentTimestamp,
  getDateTimeTimestamp,
  getTodayDateTimeISO,
} from '@/utils/date/getter.js'
import { safeMathRandom } from '@/utils/number.js'

export type AIData = SchemaAttributes<'AIResource'>

export const model = Model as ModelDefinition<AIData>

export const factory = Factory.extend<AIData>({
  resource_id() {
    return Math.floor(safeMathRandom() * 10000) + 1
  },
  status() {
    return rand(['NEW', 'IN_PROGRESS', 'COMPLETED', 'FAILED'])
  },
  response_type() {
    return rand([
      'DISCHARGE_NOTE',
      'PATIENT_HISTORY',
      'TEXT_REFINEMENT',
    ]) as AIData['response_type']
  },
  response() {
    return randTextRange({ min: 300, max: 500 })
  },
  generated_at() {
    return randPastDate().toISOString()
  },
})

export function routes(server: MockServer) {
  server.post('/ai/patient_history', function (schema, request) {
    const body = JSON.parse(request.requestBody)
    const attributes = body.data?.attributes || {}
    const forceRegenerate = request.queryParams.force_regenerate === 'true'

    if (!attributes.resource_id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'resource_id is required' }],
      })
    }

    // Check if we already have a patient history for this resource_id
    const existingHistory = schema.where('ai', {
      resource_id: attributes.resource_id,
      response_type: 'PATIENT_HISTORY',
    }).models[0]

    if (existingHistory && !forceRegenerate) {
      // Return existing history
      return existingHistory
    }

    // If force regenerate, remove existing history
    if (existingHistory && forceRegenerate) {
      existingHistory.destroy()
    }

    // Create new AI patient history request with creation timestamp
    const aiRequest = server.create('ai', {
      resource_id: attributes.resource_id,
      response_type: 'PATIENT_HISTORY',
      status: 'NEW',
      response: null,
      generated_at: null,
      // Store creation time for status progression logic
      created_at: getTodayDateTimeISO(),
    })

    return new Response(201, {}, aiRequest)
  })

  server.post('/ai/consultation_discharge_note', function (schema, request) {
    const body = JSON.parse(request.requestBody)
    const attributes = body.data?.attributes || {}

    if (!attributes.resource_id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'resource_id is required' }],
      })
    }

    // Check if we already have a discharge note for this resource_id
    const existingDischargeNote = schema.where('ai', {
      resource_id: attributes.resource_id,
      response_type: 'DISCHARGE_NOTE',
    }).models[0]

    if (existingDischargeNote) {
      // Return existing discharge note (useful for testing with pre-created records)
      return existingDischargeNote
    }

    // Create new AI consultation discharge note request with creation timestamp
    const aiRequest = server.create('ai', {
      resource_id: attributes.resource_id,
      response_type: 'DISCHARGE_NOTE',
      status: 'NEW',
      response: null,
      generated_at: null,
      // Store creation time for status progression logic
      created_at: getTodayDateTimeISO(),
    })

    return new Response(201, {}, aiRequest)
  })

  server.post('/ai/text_refinement', function (schema, request) {
    const body = JSON.parse(request.requestBody)
    const attributes = body.data?.attributes || {}

    if (!attributes.text) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'text is required' }],
      })
    }

    if (!attributes.action) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'action is required' }],
      })
    }

    // Check if we already have a text refinement for this text+action combination
    // This allows tests to pre-create COMPLETED responses
    const existingRefinement = schema.where('ai', {
      response_type: 'TEXT_REFINEMENT',
    }).models[0]

    if (existingRefinement) {
      // Return existing refinement (useful for testing with pre-created records)
      return existingRefinement
    }

    const { action, selection_type } = attributes
    let refinedText = ''

    // For partial selections, return single word/phrase refinements
    if (selection_type === 'partial') {
      switch (action) {
        case 'refine':
          refinedText = '(refined text)'
          break
        case 'shorten':
          refinedText = '(shortened text)'
          break
        case 'elaborate':
          refinedText = '(elaborated text)'
          break
        default:
          refinedText = '(refined text)'
      }
    } else {
      // For full selections, return complete HTML responses
      switch (action) {
        case 'refine':
          refinedText =
            '<p>The patient presented with mild symptoms during the clinical examination. All vital signs were recorded and found to be within normal parameters. A comprehensive treatment plan has been established.</p>'
          break

        case 'shorten':
          refinedText =
            '<p>Patient examined with normal vital signs recorded.</p>'
          break

        case 'elaborate':
          refinedText =
            '<p>The patient presented with mild symptoms during the clinical examination. All vital signs were recorded and found to be within normal parameters. A comprehensive treatment plan has been established. The examination revealed no significant abnormalities, and all findings were consistent with the initial assessment. Follow-up care has been scheduled to monitor progress and ensure optimal recovery.</p>'
          break

        default:
          refinedText = '<p>Text refinement completed.</p>'
      }
    }

    // Store the refined text in the response field so it can be delivered
    // asynchronously through the GET endpoint's status progression logic
    const aiRequest = server.create('ai', {
      resource_id: Math.floor(safeMathRandom() * 10000) + 1,
      response_type: 'TEXT_REFINEMENT',
      status: 'NEW',
      response: refinedText,
      generated_at: null,
      created_at: getTodayDateTimeISO(),
    })

    return new Response(201, {}, aiRequest)
  })

  server.get(
    '/ai/:id',
    function (schema, request) {
      const { id } = request.params

      const aiResponse = schema.find('ai', id)

      if (!aiResponse) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'AI response not found' }],
        })
      }

      // Only apply time-based progression if status is NEW or IN_PROGRESS
      // Preserve existing COMPLETED/FAILED statuses (e.g., from tests or seeds)
      if (aiResponse.status === 'NEW' || aiResponse.status === 'IN_PROGRESS') {
        // Calculate status based on time elapsed since creation
        const createdAtTimestamp = getDateTimeTimestamp(
          aiResponse.created_at || aiResponse.attrs.created_at,
        )
        const nowTimestamp = getCurrentTimestamp()
        const elapsedMs = nowTimestamp - createdAtTimestamp

        // Status progression based on elapsed time
        if (elapsedMs < 1500) {
          // First 1.5 seconds: NEW
          aiResponse.update({ status: 'NEW' })
        } else if (elapsedMs < 3000) {
          // 1.5-3 seconds: IN_PROGRESS
          aiResponse.update({ status: 'IN_PROGRESS' })
        } else {
          // After 3 seconds: COMPLETED or FAILED
          const isSuccess = safeMathRandom() > 0.1 // 90% success rate

          if (isSuccess) {
            // Use pre-generated response if available (e.g., TEXT_REFINEMENT),
            // otherwise generate random text (e.g., PATIENT_HISTORY, DISCHARGE_NOTE)
            const finalResponse =
              aiResponse.response || randTextRange({ min: 300, max: 500 })

            aiResponse.update({
              status: 'COMPLETED',
              response: finalResponse,
              generated_at: getTodayDateTimeISO(),
            })
          } else {
            aiResponse.update({
              status: 'FAILED',
              response: null,
              generated_at: null,
            })
          }
        }
      }

      return aiResponse
    },
    { timing: 500 },
  )
}

export function seeds(server: MockServer) {
  server.create('ai', {
    resource_id: 1001,
    response_type: 'PATIENT_HISTORY',
    status: 'COMPLETED',
    response: randTextRange({ min: 300, max: 500 }),
    generated_at: randPastDate().toISOString(),
  })

  server.create('ai', {
    resource_id: 1002,
    response_type: 'PATIENT_HISTORY',
    status: 'IN_PROGRESS',
    response: null,
    generated_at: null,
  })

  server.create('ai', {
    resource_id: 1003,
    response_type: 'DISCHARGE_NOTE',
    status: 'COMPLETED',
    response: randTextRange({ min: 300, max: 500 }),
    generated_at: randPastDate().toISOString(),
  })

  server.create('ai', {
    resource_id: 1004,
    response_type: 'PATIENT_HISTORY',
    status: 'FAILED',
    response: null,
    generated_at: null,
  })

  server.create('ai', {
    resource_id: 1005,
    response_type: 'TEXT_REFINEMENT',
    status: 'COMPLETED',
    response:
      '<p>The patient presented with mild symptoms during the clinical examination. All vital signs were recorded and found to be within normal parameters.</p>',
    generated_at: randPastDate().toISOString(),
  })

  server.create('ai', {
    resource_id: 1006,
    response_type: 'TEXT_REFINEMENT',
    status: 'IN_PROGRESS',
    response: null,
    generated_at: null,
  })

  server.create('ai', {
    resource_id: 1007,
    response_type: 'TEXT_REFINEMENT',
    status: 'FAILED',
    response: null,
    generated_at: null,
  })
}
